import { Injectable } from '@angular/core';
import { ApiClass } from '@data/class/api-class';
import { environment } from '@env/environment';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { IDrippingListResponse } from '../../interfaces/api-response.interface';
import { IResponseArray } from '@data/interfaces/iresponseArray.interface';
import { IMasterTemplate } from '../../interfaces/master-template.interface';

/**
 * Servicio API para gestión de plantillas maestras del módulo Dripping
 */
@Injectable({
  providedIn: 'root'
})
export class ApiTemplatesService extends ApiClass {
  private templatesCache$: Observable<IMasterTemplate[]> | null = null;

  constructor() {
    super();
    this.path = 'dripping/templates';
  }

  getAll(page = 1, limit = 10, filters = {}) {
    const response: any = {};

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters }).pipe(
      map((r) => {
        const result = r as IDrippingListResponse;
        // Transformar datos para agregar campo calculado activeStatus
        if (result.data?.data) {
          result.data.data = result.data.data.map((item: any) => ({
            ...item,
            activeStatus: item.active ? 'true' : 'false'
          }));
        }
        return result;
      }),
      catchError((e) => of(response))
    );
  }

  /**
   * Obtiene todas las plantillas activas con cache
   * Las plantillas se cargan solo una vez y se reutilizan en memoria
   * @param forceRefresh - Forzar recarga desde el servidor
   * @returns Observable con array de plantillas activas
   */
  getAllCached(forceRefresh = false): Observable<IMasterTemplate[]> {
    if (!this.templatesCache$ || forceRefresh) {
      this.templatesCache$ = this.getAll(1, 100).pipe(
        map((response: any) => {
          if (!response.error) {
            const allTemplates = response.data?.data || response.data || [];
            // Filtrar solo plantillas activas
            return allTemplates.filter((t: IMasterTemplate) => t.active);
          }
          return [];
        }),
        shareReplay(1) // Cache en memoria - comparte la misma respuesta con todos los suscriptores
      );
    }
    return this.templatesCache$;
  }

  /**
   * Limpia el cache de plantillas (útil después de crear/editar/eliminar)
   */
  clearCache(): void {
    this.templatesCache$ = null;
  }

  /**
   * Activar plantilla (cambiar active = true)
   * @param id - ID de la plantilla
   * @returns Observable con respuesta del backend
   */
  activate(id: string): Observable<any> {
    return this.postCollection({}, `${id}/activate`);
  }

  /**
   * Desactivar plantilla (cambiar active = false)
   * @param id - ID de la plantilla
   * @returns Observable con respuesta del backend
   */
  deactivate(id: string): Observable<any> {
    return this.postCollection({}, `${id}/deactivate`);
  }
}
